import axios from 'axios';
import { clearAccessToken, getAccessToken, setAccessToken } from './authTokenStore';
import { CONSENT_STORAGE_KEY } from './cookieConsent';

const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
let refreshPromise = null;

export async function refreshAccessToken() {
  if (!backendUrl) {
    return null;
  }
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${backendUrl}/auth/refresh`, {}, { withCredentials: true })
      .then((response) => {
        const token = response.data?.access_token;
        if (token) {
          setAccessToken(token);
        }
        return token || null;
      })
      .catch((error) => {
        const code = error?.response?.data?.detail?.code;
        if (code === 'AUTH_SESSION_IDLE_TIMEOUT') {
          const frontendUrl = import.meta.env.VITE_FRONTEND_URL || '';
          window.location.href = `${frontendUrl}/signin?reason=idle`;
        }
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

export async function logoutSession() {
  const token = getAccessToken();
  try {
    await axios.post(
      `${backendUrl}/auth/logout`,
      {},
      {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      },
    );
  } catch {
    // Best-effort server logout; client state is always cleared below.
  }
  clearAccessToken();
}

/** Revoke server session (refresh cookie) then hard-navigate to sign-in. */
export async function signOutAndRedirect(redirectPath = '/signin') {
  await logoutSession();
  // Cookie consent is per-browser, not per-account, so it survives logout —
  // otherwise the banner reappears for someone who already accepted.
  const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
  localStorage.clear();
  if (consent !== null) {
    localStorage.setItem(CONSENT_STORAGE_KEY, consent);
  }
  window.location.href = redirectPath;
}

export function startActivityHeartbeat(getToken) {
  const touch = async () => {
    const token = getToken();
    if (!token) {
      return;
    }
    try {
      await axios.post(
        `${backendUrl}/auth/touch`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch {
      // Idle enforcement still happens server-side on the next authorized call.
    }
  };

  let lastActivity = Date.now();
  const markActivity = () => {
    lastActivity = Date.now();
  };

  const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
  events.forEach((eventName) =>
    window.addEventListener(eventName, markActivity, { passive: true }),
  );

  // Only touch while the user was recently active. A longer window (e.g. 5 min)
  // would keep pinging /auth/touch after they stop and prevent idle logout.
  const activityWindowMs = 60 * 1000;

  const interval = window.setInterval(() => {
    if (Date.now() - lastActivity < activityWindowMs) {
      touch();
    }
  }, 60 * 1000);

  return () => {
    window.clearInterval(interval);
    events.forEach((eventName) => window.removeEventListener(eventName, markActivity));
  };
}
